'use client';

import { Suspense, use, useEffect, useState } from 'react';
import { Cross } from '@ghosts-inc/design-system/icons';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { analytics } from '@/analytics/segment';
import { setHasSeenPreferencesModal } from '../../actions';
import { FormState } from '../../preferences/actions';
import ConfirmModal from './ConfirmModal';
import { StepContextProvider } from './StepContext';
import Wizard from './Wizard';

export default function PreferencesModal({
  categoriesPromise,
  preferencesFormPromise,
  preferencesModalPromise,
}: {
  categoriesPromise: Promise<{ [key: string]: string[] }>;
  preferencesFormPromise: Promise<FormState>;
  preferencesModalPromise: Promise<boolean>;
}): JSX.Element | null {
  const hasSeenModal = use(preferencesModalPromise);

  const [open, setOpen] = useState(!hasSeenModal);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('desktop'));

  useEffect(() => {
    if (!hasSeenModal) {
      analytics.track(AnalyticsEventNames.BUYER_PREFERENCES_MODAL_VIEWED);
    }
  }, [hasSeenModal]);

  if (hasSeenModal) {
    return null;
  }

  const onCloseModals = async () => {
    setOpen(false);
    await setHasSeenPreferencesModal();
    setShowConfirmModal(false);
  };

  return (
    <StepContextProvider>
      <ConfirmModal
        show={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        onCloseModals={onCloseModals}
      />
      <Dialog
        open={open}
        onClose={() => setShowConfirmModal(true)}
        fullScreen={!isDesktop}
        sx={{
          '& .MuiDialog-paper': {
            p: 0,
            borderRadius: { desktop: 3, mobile: 0 },
          },
        }}
      >
        <Box maxWidth={800} minWidth={{ desktop: 800 }} p={4}>
          <Stack
            direction={{ mobile: 'column-reverse', desktop: 'row' }}
            justifyContent={{ desktop: 'space-between' }}
            alignItems={'flex-start'}
            width="100%"
          >
            <Stack direction="column">
              <Typography variant="h2">Personalize your homepage</Typography>
              <Typography>
                Update your preferences for personalized inventory recommendations.
              </Typography>
            </Stack>
            <Box alignSelf="start">
              <IconButton aria-label="close" onClick={() => setShowConfirmModal(true)}>
                <Cross sx={{ fontSize: { desktop: 24, mobile: 20 } }} />
              </IconButton>
            </Box>
          </Stack>
          <Suspense>
            <Wizard
              categoriesPromise={categoriesPromise}
              preferencesFormPromise={preferencesFormPromise}
              isDesktop={isDesktop}
              handleClose={onCloseModals}
            />
          </Suspense>
        </Box>
      </Dialog>
    </StepContextProvider>
  );
}
